import {createContext, use, useCallback, useMemo, useState, type PropsWithChildren} from 'react'
import {Heading, useIsomorphicLayoutEffect} from '@primer/react'
import styles from './ForYouSubSection.module.css'

interface SectionVisibilityContextValue {
  register: () => () => void
}

const SectionVisibilityContext = createContext<SectionVisibilityContextValue | null>(null)

/**
 * A section wrapper that shows its heading only when at least one child has
 * called `useRegisterAsVisible`. When no children are visible the entire
 * section is hidden, preventing orphaned headings and layout gaps.
 */
export function ForYouSubSection({title, children}: PropsWithChildren<{title: string}>) {
  const [visibleCount, setVisibleCount] = useState(0)

  const register = useCallback(() => {
    setVisibleCount(c => c + 1)
    return () => setVisibleCount(c => c - 1)
  }, [])

  const ctx = useMemo(() => ({register}), [register])

  return (
    <SectionVisibilityContext value={ctx}>
      <section className={visibleCount === 0 ? 'd-none' : undefined}>
        <Heading as="h3" variant="small" className={styles.heading}>
          {title}
        </Heading>
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ul role="list" className={styles.list}>
          {children}
        </ul>
      </section>
    </SectionVisibilityContext>
  )
}

/**
 * Call this hook from a component that renders visible content to register it
 * with the nearest `ForYouSubSection`. The section heading is shown whenever
 * at least one child is registered.
 *
 * Consumers of ForYouItem should early return null if they have no content to show
 * as once the ForYouItem is rendered we know it is visible and should be registered
 *
 * @example
 * useRegisterAsVisible()
 */
export function useRegisterAsVisible() {
  const ctx = use(SectionVisibilityContext)
  useIsomorphicLayoutEffect(() => {
    if (!ctx) return
    return ctx.register()
  }, [ctx])
}
